'use client'

import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { buildWhatsAppUrl } from '@/lib/utils/whatsapp'
import { useBookingMode } from '@/hooks/useBookingMode'
import styles from './BookNowCTA.module.css'

type BookingMode = 'direct' | 'whatsapp' | 'hidden'
type ProductType = 'lodge' | 'adventure' | 'experience'
type Variant = 'inline' | 'section' | 'closing'

interface BookNowCTAProps {
  productId: string
  productType: ProductType
  productName: string
  bookingMode: BookingMode
  variant: Variant
  bookingUrl?: string
}

export default function BookNowCTA({
  productId,
  productType,
  productName,
  bookingMode: fallbackMode,
  variant,
  bookingUrl,
}: BookNowCTAProps) {
  const t = useTranslations('common.bookNowCta')
  const locale = useLocale() as 'en' | 'es' | 'pt'
  const mode = useBookingMode(productId, fallbackMode)

  if (mode === 'hidden') return null

  // Resolve i18n label for inline link
  const inlineLabel =
    productType === 'lodge'
      ? t('inlineReserve')
      : productType === 'adventure'
      ? t('inlineBook')
      : t('inlineExperience')

  // Booking pages live under app/(booking)/ without locale prefix
  const resolvedUrl = bookingUrl || '/booking/lodges/'

  // Map productType for WhatsApp message templates
  const whatsappProductType =
    productType === 'adventure' ? 'activity' : productType

  // Build WhatsApp URL for whatsapp mode
  const whatsappUrl = buildWhatsAppUrl({
    productName,
    productType: whatsappProductType,
    locale,
  })

  // Resolve the href based on mode
  const href = mode === 'direct' && bookingUrl ? resolvedUrl : whatsappUrl
  const isExternal = mode === 'whatsapp'

  if (variant === 'inline') {
    return (
      <div className={styles.inlineWrapper}>
        {isExternal ? (
          <a href={href} target="_blank" rel="noopener noreferrer" className={styles.inlineLink}>
            {inlineLabel} &rarr;
          </a>
        ) : bookingUrl ? (
          <Link href={href} className={styles.inlineLink}>
            {inlineLabel} &rarr;
          </Link>
        ) : null}
      </div>
    )
  }

  if (variant === 'section') {
    return (
      <div className={styles.sectionWrapper}>
        <div className={styles.sectionInner}>
          <h2 className={styles.sectionTitle}>{t('sectionTitle')}</h2>
          <p className={styles.sectionText}>{t('sectionText')}</p>
          {isExternal ? (
            <a href={href} target="_blank" rel="noopener noreferrer" className={styles.sectionButton}>
              {t('sectionButton')}
            </a>
          ) : bookingUrl ? (
            <Link href={href} className={styles.sectionButton}>
              {t('sectionButton')}
            </Link>
          ) : null}
        </div>
      </div>
    )
  }

  // closing variant
  return (
    <div className={styles.closingWrapper}>
      <div className={styles.closingInner}>
        <h2 className={styles.closingTitle}>
          {t.rich('closingTitle', { em: (chunks) => <em>{chunks}</em> })}
        </h2>
        <p className={styles.closingText}>{t('closingText')}</p>
        {isExternal ? (
          <a href={href} target="_blank" rel="noopener noreferrer" className={styles.closingButton}>
            {t('closingButton')}
          </a>
        ) : bookingUrl ? (
          <Link href={href} className={styles.closingButton}>
            {t('closingButton')}
          </Link>
        ) : null}
      </div>
    </div>
  )
}
